import React from 'react';
import { Tournament, Player } from '../types';
import { Plus, Calendar, Users, Trophy, History, Clock, AlertCircle, Trash2, Edit2, Target, Swords } from 'lucide-react';

interface TournamentSelectorProps {
  tournaments: Tournament[];
  players: Player[];
  selectedTournament: Tournament | null;
  onTournamentSelect: (tournament: Tournament) => void;
  onCreateTournament: () => void;
  onDeleteTournament?: (tournament: Tournament) => void;
  onTournamentUpdate?: (tournament: Tournament) => void;
  isAdmin: boolean;
}

export function TournamentSelector({
  tournaments,
  players,
  selectedTournament,
  onTournamentSelect,
  onCreateTournament,
  onDeleteTournament,
  onTournamentUpdate,
  isAdmin
}: TournamentSelectorProps) {
  // Group tournaments by status
  const draftTournaments = tournaments.filter(t => t.status === 'draft');
  const activeTournaments = tournaments.filter(t => t.status === 'active');
  const registrationTournaments = tournaments.filter(t => t.status === 'registration');
  const completedTournaments = tournaments.filter(t => t.status === 'completed');

  const getStatusBadgeClass = (status: Tournament['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-amber-100 text-amber-800';
      case 'registration':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderTournamentList = (tournamentList: Tournament[], title: string, icon: React.ReactNode) => {
    if (tournamentList.length === 0) return null;

    return (
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
          {icon}
          {title} ({tournamentList.length})
        </h3>
        <div className="space-y-3">
          {tournamentList.map(tournament => (
            <button
              key={tournament.id}
              onClick={() => onTournamentSelect(tournament)}
              className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                selectedTournament?.id === tournament.id && isAdmin
                  ? 'border-primary-500 bg-primary-50'
                  : tournament.status === 'draft' && !isAdmin
                    ? 'border-gray-200 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-primary-500'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    {selectedTournament?.id === tournament.id && isAdmin ? (
                      <input
                        type="text"
                        value={tournament.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onTournamentUpdate?.({
                          ...tournament,
                          name: e.target.value
                        })}
                        className="font-medium bg-transparent border-none focus:ring-0 focus:outline-none p-0"
                      />
                    ) : (
                      <div className="font-medium">{tournament.name}</div>
                    )}
                    {isAdmin && selectedTournament?.id === tournament.id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newName = prompt('Enter new tournament name:', tournament.name);
                          if (newName && newName.trim()) {
                            onTournamentUpdate?.({
                              ...tournament,
                              name: newName.trim()
                            });
                          }
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 flex items-center gap-4 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {tournament.startDate}
                    </span>
                    <span className="flex items-center gap-1">
                     <span className="flex items-center gap-1">
                       {tournament.tournamentType === 'matchplay-v2' ? (
                         <Target className="w-4 h-4 text-primary-600" />
                       ) : tournament.format === 'match' ? (
                         <Swords className="w-4 h-4" />
                       ) : (
                         <Users className="w-4 h-4" />
                       )}
                       {tournament.tournamentType === 'matchplay-v2' 
                         ? 'Matchplay v2' 
                         : tournament.format === 'stroke' 
                           ? 'Stroke Play' 
                           : 'Match Play'
                       }
                     </span>
                     {players.filter(p => p.tournamentId === tournament.id).length > 0 && (
                       <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                         {players.filter(p => p.tournamentId === tournament.id).length} Players
                       </span>
                     )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(tournament.status)}`}>
                  {tournament.status}
                </span>
                {isAdmin && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent tournament selection
                      onDeleteTournament?.(tournament);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete tournament"
                  >
                    <Trash2 className="w-4 h-4" />
                  </div>
                )}
                </div>
                </div>
                {tournament.status === 'draft' && (
                  <div className="text-xs text-amber-600 mt-2">
                    {isAdmin ? 'Click Setup to configure' : 'Pending activation'}
                  </div>
                )}
              </div>
              {tournament.registrationDeadline && (
                <div className="mt-2 text-sm text-amber-600 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Registration closes: {(() => {
                    const date = new Date(tournament.registrationDeadline);
                    // Subtract 6 hours to convert from UTC to CST
                    date.setHours(date.getHours() - 6);
                    return date.toLocaleString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: '2-digit',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZoneName: 'short'
                    });
                  })()}
                </div>
              )}
              {tournament.playerLimit && (
                <div className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Player limit: {tournament.playerLimit}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6" />
          <h2 className="text-xl font-semibold text-gray-900">
            Tournaments
          </h2>
        </div>
        {isAdmin && (
          <button
            onClick={onCreateTournament}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            New Tournament
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Only show draft tournaments to admin */}
        {isAdmin && renderTournamentList(
          draftTournaments,
          'Draft Tournaments',
          <AlertCircle className="w-4 h-4" />
        )}

        {renderTournamentList(
          registrationTournaments,
          'Registration Open',
          <Users className="w-4 h-4" />
        )}

        {renderTournamentList(
          activeTournaments,
          'Active Tournaments',
          <Trophy className="w-4 h-4" />
        )}

        {renderTournamentList(
          completedTournaments,
          'Completed Tournaments',
          <History className="w-4 h-4" />
        )}

        {tournaments.length === 0 && (
          <div className="text-center py-12 px-4">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Tournaments Available
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              {isAdmin 
                ? 'Click the button below to create a new tournament and start managing players and scores.'
                : 'No tournaments are available at the moment. Please check back later.'}
            </p>
            {!isAdmin && (
              <div className="text-sm text-amber-600 mb-6 flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Contact an administrator to activate tournaments
              </div>
            )}
            {isAdmin && (
              <button
                onClick={onCreateTournament}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create First Tournament
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}